/**
 * Adds the 'Preorder' delivery message line item property to preorder cart
 * lines that were added outside the theme's product forms (e.g. Rebuy
 * upsells), which never submit the hidden input from
 * snippets/preorder-as-line-prop.liquid.
 *
 * Which lines are preorders, and their messages, is worked out server side by
 * snippets/preorder-cart-messages.liquid: inlined into the page on load, and
 * fetched via sections/preorder-cart-messages.liquid after cart changes.
 *
 * Runs on every 'theme:cart:change' (which theme.js also fires after Rebuy's
 * 'rebuy:cart.change').
 */
(function () {
  const SECTION_ID = 'preorder-cart-messages';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Line keys already checked and found not to be preorders. Keys change with
  // a line's properties, so this only skips lines that are unchanged.
  const checked = new Set();
  let running = false;

  const unchecked = (cart) =>
    cart.items.filter((item) => !(item.properties && item.properties.Preorder) && !checked.has(item.key));

  function readMessages(script) {
    try {
      return script ? JSON.parse(script.textContent) : {};
    } catch (err) {
      return {};
    }
  }

  async function fetchMessages() {
    const res = await fetch(`${root}cart?sections=${SECTION_ID}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const sections = await res.json();
    const html = new DOMParser().parseFromString(sections[SECTION_ID] || '', 'text/html');
    return readMessages(html.querySelector('[data-preorder-cart-messages]'));
  }

  /**
   * @param {Object} cart Cart JSON
   * @param {Object} [messages] Line key => message, fetched if not passed
   */
  async function addMissingProperties(cart, messages) {
    if (running || !cart || !cart.items) return;
    const candidates = unchecked(cart);
    if (!candidates.length) return;
    running = true;
    let changed = false;

    try {
      messages = messages || (await fetchMessages());

      for (const { key } of candidates) {
        if (!messages[key]) {
          checked.add(key);
          continue;
        }

        // Find the line in the latest cart, as each change returns a new one
        const item = cart.items.find((line) => line.key === key);
        if (!item) continue;

        // properties replaces the line's whole set, so keep the existing ones.
        // Preorder goes first so it displays before Notify Me's 'Type', as it
        // does for lines added via the product forms.
        const { Preorder, ...properties } = item.properties || {};
        const res = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: { Preorder: messages[key], ...properties },
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        cart = await res.json();
        changed = true;
      }
    } catch (err) {
      console.error('[preorder-line-props] Failed to add Preorder property:', err);
    } finally {
      running = false;
    }

    if (!changed) return;

    document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart }, bubbles: true }));

    // Re-render the cart drawer so the property shows. On the cart page this
    // event reloads the whole page, so leave it to show on next visit there.
    if (!/\/cart\/?$/.test(window.location.pathname)) {
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    }
  }

  document.addEventListener('theme:cart:change', (event) => {
    addMissingProperties(event.detail && event.detail.cart);
  });

  // Lines already in the cart on page load, from the inlined snippet output
  const initial = readMessages(document.querySelector('[data-preorder-cart-messages-initial]'));
  if (Object.keys(initial).length) {
    fetch(`${root}cart.js`)
      .then((res) => res.json())
      .then((cart) => addMissingProperties(cart, initial))
      .catch((err) => console.error('[preorder-line-props] Failed to load cart:', err));
  }
})();
